import merge from 'lodash/merge';
import set from 'lodash/set';
import ChartParameter from './const/ChartParameter';
import Base from '../vcom/Base';
import EventService from '../common/event/EventService';
import PortalEvent from '../vcom/events/PortalEvent';
import translate from '../common/localization/translate';
import VcomProvider from '../VcomProvider';
import VcomSection from '../vcom/const/VcomSection';

export default function DateRangePickerExtended($, moment) {
  let datepicker;
  let pickerDefaultOptions;
  let pickerElement;
  let rangePickMode = false;
  const savedPeriods = {};
  let previouslySelectedPeriod;

  return {
    init(params) {
      pickerElement = $(params.parentEl);
      pickerDefaultOptions = params;
      createDatepicker(pickerDefaultOptions);
    },
    extendParamsByPickersStartAndEndDate,
    bindListeners,
    modifyStartAndEnd,
    modifyStartAndEndByPrevControl,
    modifyStartAndEndByNextControl,
    prepareDateControls,
    setToday,
    setRangePickMode,
    createDatepicker,
    isSingleDatePicker,
    isRangePickMode,
    getDatepicker,
  };

  function extendParamsByPickersStartAndEndDate(options) {
    if (options.dateTo) {
      delete options.dateTo;
    }

    if (isRangePickMode()) {
      options.date = datepicker.startDate
        .set({ hour: 0, minute: 0, second: 0 })
        .format('YYYY-MM-DDTHH:mm:ss');

      options.dateTo = datepicker.endDate
        .set({ hour: 23, minute: 59, second: 59 })
        .format('YYYY-MM-DDTHH:mm:ss');

      return options;
    }

    options.date = datepicker.endDate
      .set({ hour: 0, minute: 0, second: 0 })
      .format('YYYY-MM-DDTHH:mm:ss');

    return options;
  }

  function saveRangeForPeriod(period, startDate, endDate) {
    if (VcomProvider.section === VcomSection.EVALUATION) {
      const date = period === 'woche' ? endDate.format() : startDate.format();
      Base.setUrlQueryParameter(ChartParameter.DATE, date);
      Base.setUrlQueryParameter(ChartParameter.END_DATE, endDate.format());
    }

    set(savedPeriods, period, {
      startDate: startDate.clone(),
      endDate: endDate.clone(),
    });
  }

  function retrieveRangeForPeriod(period, date) {
    if (date) {
      return {
        startDate: moment(date),
        endDate: moment(date),
      };
    }

    const startDate = datepicker.startDate.clone();
    const endDate = datepicker.endDate ? datepicker.endDate.clone() : datepicker.startDate.clone();

    const hasPeriodChanged = previouslySelectedPeriod !== undefined
      && previouslySelectedPeriod !== period;
    if (savedPeriods[period] && hasPeriodChanged) {
      return {
        startDate: savedPeriods[period].startDate,
        endDate: savedPeriods[period].endDate,
      };
    }
    return {
      startDate,
      endDate,
    };
  }

  function modifyStartAndEnd(period, date) {
    const { startDate, endDate } = retrieveRangeForPeriod(period, date);

    switch (period) {
      case 'tag':
        startDate.startOf('day');
        endDate.endOf('day');
        break;

      case 'woche':
        saveRangeForPeriod(period, startDate, endDate);
        startDate.subtract(6, 'days');
        break;

      case 'monat':
        startDate.startOf('month');
        endDate.endOf('month');
        break;

      case 'jahr':
        startDate.startOf('year');
        endDate.endOf('year');
        break;

      default:
      // do nothing
    }
    if (period !== 'woche') {
      saveRangeForPeriod(period, startDate, endDate);
    }
    previouslySelectedPeriod = period;
    updateDatepicker(startDate, endDate);
    toggleDisabledStateOnNextControl(period);
    datepicker.clickApply();
  }

  function modifyStartAndEndByPrevControl(period) {
    const startDate = datepicker.startDate.clone();
    let endDate = datepicker.endDate.clone();

    switch (period) {
      case 'tag':
        startDate.subtract(1, 'days');
        endDate.subtract(1, 'days');
        break;

      case 'woche':
        startDate.subtract(7, 'days');
        endDate = moment(startDate).add(6, 'days');
        break;

      case 'monat':
        startDate.subtract(1, 'months').startOf('month');
        endDate = moment(startDate).endOf('month');
        break;

      case 'jahr':
        startDate.subtract(1, 'years').startOf('year');
        endDate = moment(startDate).endOf('year');
        break;

      default:
      // do nothing
    }
    saveRangeForPeriod(period, startDate, endDate);
    updateDatepicker(startDate, endDate);
    toggleDisabledStateOnNextControl(period);
    datepicker.clickApply();
  }

  function modifyStartAndEndByNextControl(period) {
    let startDate = datepicker.startDate.clone();
    let endDate = datepicker.endDate.clone();

    switch (period) {
      case 'tag':
        startDate.add(1, 'days');
        endDate.add(1, 'days');
        break;

      case 'woche':
        endDate.add(7, 'days');
        startDate = moment(endDate).subtract(6, 'days');
        break;

      case 'monat':
        startDate.add(1, 'months').startOf('month');
        endDate = moment(startDate).endOf('month');
        break;

      case 'jahr':
        startDate.add(1, 'years').startOf('year');
        endDate = moment(startDate).endOf('year');
        break;

      default:
      // do nothing
    }
    saveRangeForPeriod(period, startDate, endDate);
    updateDatepicker(startDate, endDate);
    toggleDisabledStateOnNextControl(period);
    datepicker.clickApply();
  }

  function bindListeners(scope, options) {
    const parent = pickerElement.parent();
    parent.on('pickedStart.daterangepicker', 'div.chart-datepicker', function (event) {
      const period = options.periodSelection
        ? options.periodSelection.getCurrentPeriod()
        : options.period;

      if (['gesamt', 'custom'].includes(period)) {
        return;
      }
      modifyStartAndEnd(period);
      const updateFunc = options.updateFunc ?? null;
      scope.chart('triggerChartUpdate', updateFunc, scope, { period });
    });

    parent.on('pickedEnd.daterangepicker', 'div.chart-datepicker', function (event) {
      const period = options.periodSelection
        ? options.periodSelection.getCurrentPeriod()
        : options.period;

      modifyStartAndEnd(period);
      const updateFunc = options.updateFunc ?? null;
      scope.chart('triggerChartUpdate', updateFunc, scope, { period });
    });

    pickerElement.on('show.daterangepicker showCalendar.daterangepicker', function (ev, picker) {
      movePickerToParent(picker);
    });

    EventService.addListener(PortalEvent.DATEPICKERS_CHANGED_TO_TODAY, {
      callback() {
        if (options.chart.key === 'SPF01') {
          return;
        }
        setToday();
      },
    });
  }

  function movePickerToParent(picker) {
    if (picker.parentEl.is('body')) {
      return;
    }
    if ($('.portlet-head').length) {
      picker.container.offset({
        left: picker.element.offset().left - (picker.element.width() / 1.3),
        top: picker.container.offset().top,
      });
    } else {
      picker.container.offset({
        top: picker.container.offset().top,
      });
    }
  }

  function setToday() {
    modifyStartAndEnd(previouslySelectedPeriod, moment());
  }

  function prepareDateControls(data, response) {
    const shouldDisplayControls = (response.datetime !== undefined);
    const [prevButton, nextButton] = data.opts.prevNextControls;
    prevButton.toggle(shouldDisplayControls);
    nextButton.toggle(shouldDisplayControls);
    datepicker.element.toggle(shouldDisplayControls);

    if (shouldDisplayControls === false) {
      return;
    }

    preparePrevNextControlsHidingByPeriod(data.opts.prevNextControls, response.period);
    prepareChartHeaderDisplayDate(data, response);

    if (datepicker) {
      if (!response.isCustomRange) {
        prepareDatapickerStartAndEnd(response);
      }
      if (['jahr', 'gesamt'].includes(response.period)) {
        datepicker.element.disableSelection();
      }
    }

    toggleDisabledStateOnNextControl(response.period);

    datepicker.element.trigger('preparedDateControls.daterangepicker');
  }

  function getEnglishPeriod(period) {
    switch (period) {
      case 'tag':
        return 'day';
      case 'woche':
        return 'week';
      case 'monat':
        return 'month';
      case 'jahr':
        return 'year';
      default:
        return period;
    }
  }

  function toggleDisabledStateOnNextControl(period) {
    const nextBtn = pickerElement.parent().find('[name=datepicker-next-button]');
    if (nextBtn) {
      nextBtn.prop('disabled', datepicker.endDate.isSame(datepicker.maxDate, getEnglishPeriod(period)));
    }
  }

  function createDatepicker(options) {
    options = merge({}, pickerDefaultOptions, options);
    datepicker = pickerElement.daterangepicker(merge({
      autoApply: true,
      startDate: options.startDate || moment(),
      endDate: options.endDate || moment(),
      showDropdowns: true,
      linkedCalendars: false,
      maxDate: moment(),
    }, options)).data('daterangepicker');
  }

  function isSingleDatePicker() {
    return datepicker.isSingleDatePicker();
  }

  function isRangePickMode() {
    return rangePickMode;
  }

  function getDatepicker() {
    return datepicker;
  }

  function setRangePickMode(val) {
    rangePickMode = val;
  }

  function preparePrevNextControlsHidingByPeriod(prevNextControls, period) {
    $.each(prevNextControls, function (i, control) {
      control.toggle(period !== 'gesamt' && period !== 'custom');
    });
  }

  function prepareChartHeaderDisplayDate(data, response) {
    const periodSelection = data.opts.periodSelection || {};
    const displayDateTime = buildChartHeaderDisplayDate(response, periodSelection);
    const headerDisplayDateElement = data.opts.displayDatetime
      ? data.opts.displayDatetime
      : $('.chartDisplayDatetime', data.target);

    headerDisplayDateElement.html(displayDateTime);
  }

  function prepareDatapickerStartAndEnd(response) {
    const startDate = moment(response.datetime, 'YYYY-MM-DD');
    let endDate = startDate.clone();
    const dateTimeEnd = response.datetimeEnd;

    if (dateTimeEnd) {
      endDate = moment(dateTimeEnd, 'YYYY-MM-DD');
    }
    datepicker.setStartDate(startDate);
    datepicker.setEndDate(endDate);
  }

  function buildChartHeaderDisplayDate(response, periodSelection) {
    let displayDateTime = response.datetimeLoc;
    const startDate = moment(response.datetime, 'YYYY-MM-DD');

    if (isRangePickMode()) {
      if (response.period === 'jahr') {
        const endDate = moment(response.datetimeEnd, 'YYYY-MM-DD');
        updateDatepicker(startDate, endDate);
      }
      return `${displayDateTime} - ${response.datetimeLocEnd}`;
    }

    switch (response.period) {
      case 'tag':
        break;
      case 'woche':
        displayDateTime += ` - ${response.datetimeLocEnd}`;
        break;
      case 'monat':
        displayDateTime = startDate.format('MMM YYYY');
        break;
      case 'jahr':
        displayDateTime = startDate.format('YYYY');
        break;
      case 'gesamt':
        displayDateTime = translate('javascript.TX_TOTAL');
        break;
      default:
      // do nothing
    }
    return displayDateTime;
  }

  function updateDatepicker(start, end) {
    datepicker.setStartDate(start);
    datepicker.setEndDate(end);
    datepicker.updateView();
  }
}
